import React, { useState } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Badge,
  ExpandableSection,
  Box,
} from '@amzn/awsui-components-console';

export interface RequirementSection {
  title: string;
  items: string[];
}

export interface RequirementButton {
  label: string;
  style: 'primary' | 'normal';
  action: string;
  onClick?: () => void;
}

export interface RequirementReviewCardProps {
  title: string;
  useCaseId?: string;
  objective?: string;
  status?: string;
  sections: RequirementSection[];
  buttons: RequirementButton[];
  onButtonClick?: (action: string) => void;
}

const STATUS_COLOR_MAP: Record<string, 'blue' | 'green' | 'red' | 'grey'> = {
  DRAFT: 'grey',
  IN_REVIEW: 'blue',
  APPROVED: 'green',
  REJECTED: 'red',
  COMPLETE: 'green',
};

export const RequirementReviewCard: React.FC<RequirementReviewCardProps> = ({
  title,
  useCaseId,
  objective,
  status,
  sections,
  buttons,
  onButtonClick,
}) => {
  const [clickedAction, setClickedAction] = useState<string | null>(null);

  const handleClick = (btn: RequirementButton) => {
    setClickedAction(btn.action);
    btn.onClick?.();
    onButtonClick?.(btn.action);
  };

  const badgeColor = status ? STATUS_COLOR_MAP[status.toUpperCase()] ?? 'grey' : undefined;

  return (
    <div className="requirement-review-card">
      <Container
        header={
          <Header
            variant="h3"
            description={useCaseId ? `Use Case: ${useCaseId}` : undefined}
            info={
              status ? (
                <Badge color={badgeColor}>{status}</Badge>
              ) : undefined
            }
          >
            {title}
          </Header>
        }
      >
        <SpaceBetween size="m">
          {objective && (
            <Box variant="p" color="text-body-secondary">
              {objective}
            </Box>
          )}

          {sections.map((section, idx) => (
            <ExpandableSection
              key={idx}
              headerText={section.title}
              defaultExpanded
              variant="footer"
            >
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {section.items.map((item, i) => (
                  <li key={i} style={{ marginBottom: '4px', fontSize: '13px' }}>
                    {item}
                  </li>
                ))}
              </ul>
            </ExpandableSection>
          ))}

          {buttons.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              {buttons.map((btn, i) => (
                <Button
                  key={i}
                  variant={btn.style === 'primary' ? 'primary' : 'normal'}
                  onClick={() => handleClick(btn)}
                  loading={clickedAction === btn.action}
                >
                  {btn.label}
                </Button>
              ))}
            </div>
          )}
        </SpaceBetween>
      </Container>
    </div>
  );
};

export default RequirementReviewCard;
